import React, { useEffect, useState } from 'react';
import SEOHead from '../../../components/SEOHead';
import HormuzHubNav from '../../../components/HormuzHubNav';
import { chokepointsAPI } from '../../../services/api';
import {
  getOrganizationSchema,
  getBreadcrumbSchema,
  getCollectionPageSchema,
  getNewsArticleSchema,
} from '../../../utils/seoSchemas';
import { ExternalLink, Clock } from 'lucide-react';

const HormuzLiveUpdates = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 20;

  const loadNews = async (offset = 0) => {
    try {
      const res = await chokepointsAPI.getNews('hormuz', { limit, offset });
      setNews(res.data.news);
      setTotal(res.data.pagination.total);
      setLastUpdate(new Date());
    } catch (e) {
      console.error('Failed to load news', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNews(page * limit);
    // Auto-refresh every 10 minutes
    const interval = setInterval(() => {
      loadNews(page * limit);
    }, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, [page]);

  const title = 'Strait of Hormuz Live Updates | Real-Time News Feed';
  const description =
    'Real-time news and updates about the Strait of Hormuz, including shipping disruptions, oil flow changes, and geopolitical developments. Auto-updates every 10 minutes.';

  const jsonLd = [
    getOrganizationSchema(),
    getBreadcrumbSchema([
      { name: 'Home', path: '/' },
      { name: 'Strait of Hormuz', path: '/strait-of-hormuz' },
      { name: 'Live Updates', path: '/strait-of-hormuz/live-updates' },
    ]),
    getCollectionPageSchema(
      title,
      description,
      news.map((item) => ({
        type: 'NewsArticle',
        ...getNewsArticleSchema(item),
      }))
    ),
  ];

  return (
    <article className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SEOHead
        title={title}
        description={description}
        path="/strait-of-hormuz/live-updates"
        keywords="Hormuz live updates, Strait of Hormuz news, real-time Hormuz updates"
        jsonLd={jsonLd}
        image="/api/og-image/hormuz-news.png"
        imageAlt="Strait of Hormuz Live Updates - Real-time news feed"
      />

      <HormuzHubNav />

      <header className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-on-surface mb-2">
            Live Updates
          </h1>
          <p className="text-slate-600 dark:text-on-surface-variant">
            Real-time news and developments in the Strait of Hormuz
          </p>
        </div>
        {lastUpdate && (
          <div className="flex items-center text-sm text-slate-500 dark:text-on-surface-variant">
            <Clock className="w-4 h-4 mr-2" />
            Updated {lastUpdate.toLocaleTimeString()}
          </div>
        )}
      </header>

      {loading && news.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
        </div>
      ) : news.length === 0 ? (
        <div className="text-center py-12 text-slate-500 dark:text-on-surface-variant">
          No updates available at this time.
        </div>
      ) : (
        <>
          <div className="space-y-4 mb-6">
            {news.map((item) => (
              <article
                key={item.id}
                className="rounded-stitch-xl border border-slate-200/80 bg-white dark:border-outline-variant/15 dark:bg-surface-container p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-on-surface mb-2">
                      {item.headline}
                    </h3>
                    <div className="flex items-center space-x-4 text-xs text-slate-500 dark:text-on-surface-variant mb-3">
                      <span>{item.source}</span>
                      <span>•</span>
                      <time dateTime={item.published_at}>
                        {new Date(item.published_at).toLocaleString()}
                      </time>
                      {item.sentiment_score && (
                        <>
                          <span>•</span>
                          <span>Sentiment: {item.sentiment_score}/100</span>
                        </>
                      )}
                    </div>
                    <p className="text-sm text-slate-700 dark:text-on-surface-variant mb-3">
                      {item.ai_summary}
                    </p>
                  </div>
                </div>
                {item.url && (
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-sm text-primary-600 dark:text-primary hover:underline"
                  >
                    Read full article
                    <ExternalLink className="w-4 h-4 ml-1" />
                  </a>
                )}
              </article>
            ))}
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-4 py-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-sm text-slate-600 dark:text-on-surface-variant">
              Page {page + 1} of {Math.ceil(total / limit)}
            </span>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={(page + 1) * limit >= total}
              className="px-4 py-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </>
      )}
    </article>
  );
};

export default HormuzLiveUpdates;

